/**
 * Series disponibles por lotería + número (mismo contrato que la app Flutter).
 * Mensaje enviado: { lottery_id, number }
 * Respuesta: { series: string[] }
 *
 * Nota: Vercel no proxifica WebSocket vía rewrites; en producción HTTPS hace falta
 * REACT_APP_LOTTERY_SERIES_WS_URL (wss://…) o TLS en el ALB. Si no, las series pueden fallar sin bloquear el resto.
 */

import { getApiRoot, API_PROXY_UPSTREAM } from "./api";

const DEFAULT_WSS = "wss://dev.goldloterias.com/ws/lottery/series/";

export function getSeriesWebSocketUrl() {
  const explicit = process.env.REACT_APP_LOTTERY_SERIES_WS_URL;
  if (explicit && String(explicit).trim()) {
    return String(explicit).trim();
  }

  const api = getApiRoot();

  // Rutas relativas (/api, /api/proxy): WebSocket directo al host del backend
  if (api === "/api" || api === "/api-proxy" || api === "/api/proxy") {
    try {
      const upstream = new URL(API_PROXY_UPSTREAM);
      const wsProto = upstream.protocol === "https:" ? "wss:" : "ws:";
      return `${wsProto}//${upstream.host}/ws/lottery/series/`;
    } catch {
      return DEFAULT_WSS;
    }
  }

  try {
    const base =
      typeof window !== "undefined"
        ? window.location.origin
        : "http://localhost:3000";
    const u = /^https?:\/\//i.test(api)
      ? new URL(api)
      : new URL(api.startsWith("/") ? api : `/${api}`, base);
    const wsProto = u.protocol === "https:" ? "wss:" : "ws:";
    return `${wsProto}//${u.host}/ws/lottery/series/`;
  } catch {
    return DEFAULT_WSS;
  }
}

export class SeriesWebSocketClient {
  constructor() {
    this._ws = null;
    this._listeners = new Set();
    this._intentionalClose = false;
  }

  subscribe(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  _emit(series) {
    const list = Array.isArray(series) ? series : [];
    this._listeners.forEach((fn) => {
      try {
        fn(list);
      } catch (e) {
        console.error("SeriesWebSocket listener error:", e);
      }
    });
  }

  connect() {
    if (
      this._ws &&
      (this._ws.readyState === WebSocket.OPEN ||
        this._ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    this._intentionalClose = false;
    const url = getSeriesWebSocketUrl();

    try {
      this._ws = new WebSocket(url);
    } catch (e) {
      console.error("SeriesWebSocket: no se pudo crear WebSocket", e);
      this._emit([]);
      return;
    }

    this._ws.onmessage = (ev) => {
      try {
        const data = JSON.parse(ev.data);
        const series = (data.series || [])
          .map((e) => String(e).trim())
          .filter(Boolean);
        this._emit(series);
      } catch (e) {
        console.warn("SeriesWebSocket: mensaje inválido", e);
        this._emit([]);
      }
    };

    this._ws.onerror = () => {
      this._emit([]);
    };

    this._ws.onclose = () => {
      if (!this._intentionalClose) {
        this._ws = null;
      }
    };
  }

  /**
   * @param {string} lotteryId - UUID de la lotería
   * @param {string} number - 4 dígitos
   */
  querySeries(lotteryId, number) {
    if (!lotteryId || !number || String(number).length !== 4) return;

    this.connect();
    const payload = JSON.stringify({
      lottery_id: lotteryId,
      number: String(number),
    });

    const sock = this._ws;
    if (!sock) return;

    const send = () => {
      if (this._ws && this._ws.readyState === WebSocket.OPEN) {
        this._ws.send(payload);
      }
    };

    if (sock.readyState === WebSocket.OPEN) {
      send();
    } else {
      sock.addEventListener("open", send, { once: true });
    }
  }

  disconnect() {
    this._intentionalClose = true;
    this._listeners.clear();
    if (this._ws) {
      try {
        this._ws.close();
      } catch {
        /* ignore */
      }
      this._ws = null;
    }
  }
}
